// features/products/packageLabel.ts
//
// Rút gọn tên gói ảnh thẻ thành thứ nhân sự cần đọc trên lịch: loại gói và
// có make hay không. Toàn bộ 10 gói ảnh thẻ đều kết thúc bằng "(có make)" /
// "(không make)", nên trên thẻ lịch chỉ thấy đuôi bị cắt — đúng phần quyết
// định makeup có phải xếp người vào ca hay không.
//
// Chỉ áp cho nhóm ảnh thẻ. Profile và doanh nghiệp luôn có makeup nên gắn
// nhãn "có/không make" ở đó là thừa và làm loãng tín hiệu.

import { PRODUCT_CATEGORIES } from "./constants";

export const IDPHOTO_CATEGORY = "idphoto";

/** Gói ảnh thẻ đã tách phần tên và phần makeup. */
export type IdPhotoPackage = {
  /** Tên gói bỏ hậu tố makeup: "Ảnh Thẻ", "CV Basic", "Tiếp viên hàng không"… */
  name: string;
  /** true = gói có trang điểm, false = không, null = tên gói không nói rõ. */
  withMakeup: boolean | null;
};

const MAKEUP_SUFFIX = /\s*\((có|khong|không)\s*make(up)?\)\s*$/i;

/**
 * Tách "CV Basic (có make)" → { name: "CV Basic", withMakeup: true }.
 * Tên không có hậu tố trả withMakeup = null (gói tự đặt thêm sau này).
 */
export function splitMakeupSuffix(productLabel: string): IdPhotoPackage {
  const match = MAKEUP_SUFFIX.exec(productLabel);
  if (!match) return { name: productLabel.trim(), withMakeup: null };
  const kind = (match[1] ?? "").toLowerCase();
  return {
    name: productLabel.slice(0, match.index).trim(),
    withMakeup: kind === "có",
  };
}

/**
 * Gói ảnh thẻ của một ca, hoặc null nếu ca không thuộc nhóm ảnh thẻ.
 *
 * `category` là nguồn quyết định, không phải id sản phẩm: đơn cũ và đơn tạo từ
 * catalog tuỳ chỉnh mang id ngoài danh mục tĩnh, nhưng category thì luôn có.
 * Khi thiếu category thì tra ngược danh mục ảnh thẻ theo id.
 */
/**
 * Tên sản phẩm hiển thị trên THẺ LỊCH cho ca không thuộc nhóm ảnh thẻ.
 * Profile và doanh nghiệp mặc định có makeup, nên đuôi "(có make)" chỉ ăn chỗ
 * trên một thẻ hẹp (CEO chốt 19/08/2026) — cắt bỏ, giống cách nhóm ảnh thẻ đã
 * làm. Đuôi "(không make)" thì GIỮ NGUYÊN: ở nhóm này chưa có nhãn riêng để
 * nói thay, cắt đi là makeup mất tín hiệu duy nhất báo ca không cần họ.
 */
export function scheduleProductLabel(productLabel: string): string {
  const pkg = splitMakeupSuffix(productLabel);
  return pkg.withMakeup === true ? pkg.name : productLabel.trim();
}

export function idPhotoPackageOf(input: {
  category?: string | null | undefined;
  product?: string | null | undefined;
  /** Tên sản phẩm đã tra sẵn (lưới lịch đã map từ id sang tên). */
  productLabel?: string | null | undefined;
}): IdPhotoPackage | null {
  const label = (input.productLabel ?? "").trim();
  if (!label) return null;
  const category = (input.category ?? "").trim();
  if (category) {
    if (category !== IDPHOTO_CATEGORY) return null;
    return splitMakeupSuffix(label);
  }
  return isIdPhotoProductId(input.product) ? splitMakeupSuffix(label) : null;
}

function isIdPhotoProductId(productId: string | null | undefined): boolean {
  if (!productId) return false;
  const cat = PRODUCT_CATEGORIES[IDPHOTO_CATEGORY];
  return !!cat?.subProducts.some((p) => p.id === productId);
}
